import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './style.css';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipAlign = 'start' | 'center' | 'end';
export type TooltipVariant = 'accent' | 'arrow';

interface TooltipProps {
  content: React.ReactNode;
  position?: TooltipPosition;
  align?: TooltipAlign;
  variant?: TooltipVariant;
  maxWidth?: number | string;
  children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({
  content,
  position = 'top',
  align = 'start',
  variant = 'accent',
  maxWidth,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const updateTooltipPosition = () => {
    if (!wrapperRef.current || !tooltipRef.current) return;

    const wrapperRect = wrapperRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = wrapperRect.top + scrollY - tooltipRect.height - 8;
        break;
      case 'bottom':
        top = wrapperRect.bottom + scrollY + 8;
        break;
      case 'left':
        left = wrapperRect.left + scrollX - tooltipRect.width - 8;
        top = wrapperRect.top + scrollY + (wrapperRect.height - tooltipRect.height) / 2;
        break;
      case 'right':
        left = wrapperRect.right + scrollX + 8;
        top = wrapperRect.top + scrollY + (wrapperRect.height - tooltipRect.height) / 2;
        break;
    }

    switch (align) {
      case 'start':
        if (position === 'top' || position === 'bottom') {
          left = wrapperRect.left + scrollX;
        } else {
          top = wrapperRect.top + scrollY;
        }
        break;
      case 'center':
        if (position === 'top' || position === 'bottom') {
          left = wrapperRect.left + scrollX + (wrapperRect.width - tooltipRect.width) / 2;
        }
        break;
      case 'end':
        if (position === 'top' || position === 'bottom') {
          left = wrapperRect.right + scrollX - tooltipRect.width;
        } else {
          top = wrapperRect.bottom + scrollY - tooltipRect.height;
        }
        break;
    }

    setTooltipPosition({ top, left });
  };

  useEffect(() => {
    if (isVisible) {
      updateTooltipPosition();
      const handleScroll = () => updateTooltipPosition();
      const handleResize = () => updateTooltipPosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isVisible, position, align]);

  const handleMouseEnter = () => {
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        ref={wrapperRef}
        className="tooltip-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>
      {isVisible && createPortal(
        <div
          ref={tooltipRef}
          className={[
            'tooltip-bubble',
            `pos-${position}`,
            `align-${align}`,
            `var-${variant}`,
          ].join(' ')}
          role="tooltip"
          style={{
            position: 'absolute',
            top: tooltipPosition.top,
            left: tooltipPosition.left,
            maxWidth: maxWidth ? (typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth) : undefined,
            zIndex: 9999,
          }}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
